import { ROUTES } from '@common/enums'
import { staticFile } from '@common/utils'
import { LazyImage } from '@components/common/image'
import Spinner from '@components/common/spinner'
import { useSessionContext } from '@contexts/session'
import { QUIZZES_IMAGE_PATH } from '@lib/constants/quizzes'
import { onCreateAccountToSave, onQuizPlay } from '@lib/utils/quizzes'
import { useTransition } from '@react-spring/core'
import { animated } from '@react-spring/web'
import Link from 'next/link'
import router from 'next/router'
import { Line } from 'rc-progress'
import { Dispatch, SetStateAction, useState } from 'react'
import { ArrowLeft } from 'react-feather'

type Props = {
  quizId: number
  slug: string
  quizTitle: string
  remainingQuestions: number
  points: number
  isPaused: boolean
  setIsPaused: Dispatch<SetStateAction<boolean>>
  totalProblems: number
  isLessonQuiz: boolean
}

const PauseMenu = ({
  quizId,
  slug,
  quizTitle,
  remainingQuestions,
  points,
  isPaused,
  setIsPaused,
  totalProblems,
  isLessonQuiz,
}: Props) => {
  const { loggedIn } = useSessionContext()
  const [isLoading, setIsLoading] = useState(false)
  const pauseMenuTransition = useTransition(isPaused, {
    from: { opacity: 0, y: -1000 },
    enter: { opacity: 1, y: 0 },
    leave: { opacity: 0, y: -1000 },
    config: { duration: 500 },
  })

  const handlePlayAgain = async () => {
    if (isLoading) return
    setIsLoading(true)
    await onQuizPlay(quizId, slug)
    // `onQuizPlay` function will redirect user to the same page. Hence we need to reload the page
    // to get the latest data
    router.reload()
  }

  return pauseMenuTransition((styles, isVisible) => (
    <>
      {isVisible && (
        <animated.div
          style={styles}
          className="absolute flex items-center justify-center w-full h-full"
        >
          <div className="flex flex-col p-6 mx-5 space-y-3 text-black bg-white rounded-lg lg:p-10">
            <h2 className="text-lg lg:text-2xl font-600">{quizTitle}</h2>
            <div className="relative pt-6">
              <Line
                percent={
                  ((totalProblems - remainingQuestions) * 100) / totalProblems
                }
                strokeColor="#4AAF50"
                trailColor="#4AAF5066"
                strokeWidth={6}
                trailWidth={6}
              />
              <img
                src={staticFile(`${QUIZZES_IMAGE_PATH}arena/finish.svg`)}
                className="absolute top-0 right-0 -mr-2"
              />
              <img
                src={staticFile(`${QUIZZES_IMAGE_PATH}arena/player.svg`)}
                className="absolute top-0 w-4 -ml-2 lg:ml-0"
                style={{
                  left: `${
                    ((totalProblems - remainingQuestions) * 100) / totalProblems
                  }%`,
                }}
              />
            </div>
            <p className="font-600 lg:text-lg">
              {remainingQuestions} question{remainingQuestions > 1 && 's'}{' '}
              remaining
            </p>
            <div className="flex items-center space-x-2">
              <LazyImage
                path={`${QUIZZES_IMAGE_PATH}details-page/`}
                name="points"
                alt="hand full of star"
                className="w-8"
              />
              <p className="text-sm font-600 lg:text-base">Points: {points}</p>
            </div>
            <div className="flex flex-wrap items-center">
              <button
                className="mr-2 btn btn-orange"
                onClick={() => setIsPaused(false)}
              >
                Resume
              </button>
              {loggedIn ? (
                <button
                  className="btn text-orange hover:bg-orange-200 active:bg-orange-300"
                  onClick={handlePlayAgain}
                >
                  {isLoading ? <Spinner classes="w-5" /> : 'Restart'}
                </button>
              ) : (
                <button
                  className="mt-2 btn text-orange hover:bg-orange-200 active:bg-orange-300 lg:mt-0"
                  onClick={onCreateAccountToSave}
                >
                  Create account to save
                </button>
              )}
            </div>
            <Link
              href={
                isLessonQuiz
                  ? ROUTES.STUDENT_DASHBOARD_QUIZZES_LIST
                  : ROUTES.QUIZZES_LIST
              }
            >
              <a className="flex space-x-2 text-orange hover:underline focus:outline-none font-600">
                <ArrowLeft />
                <span>
                  {isLessonQuiz ? 'Back to dashboard' : 'Back to quizzes'}
                </span>
              </a>
            </Link>
          </div>
        </animated.div>
      )}
    </>
  ))
}

export default PauseMenu
